import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { PARTNER_TYPES } from "@shared/schema";
import type { PartnerType } from "@shared/schema";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { PartnerApplicationNotificationSettings } from "./types";
import { parseEmailList } from "./helpers";

export function PartnerApplicationNotificationSettingsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [defaultInput, setDefaultInput] = useState("");
  const [perTypeInput, setPerTypeInput] = useState<Record<PartnerType, string>>(
    () => PARTNER_TYPES.reduce((acc, t) => { acc[t] = ""; return acc; }, {} as Record<PartnerType, string>),
  );
  const [hydrated, setHydrated] = useState(false);

  const settingsQuery = useQuery<PartnerApplicationNotificationSettings>({
    queryKey: ["/api/admin/partner-application-notifications"],
    queryFn: async () => {
      const res = await fetch("/api/admin/partner-application-notifications", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load notification settings");
      return res.json();
    },
  });

  useEffect(() => {
    if (!settingsQuery.data || hydrated) return;
    setDefaultInput((settingsQuery.data.defaultRecipients ?? []).join(", "));
    const next: Record<PartnerType, string> = PARTNER_TYPES.reduce((acc, t) => {
      acc[t] = (settingsQuery.data!.perTypeRecipients?.[t] ?? []).join(", ");
      return acc;
    }, {} as Record<PartnerType, string>);
    setPerTypeInput(next);
    setHydrated(true);
  }, [settingsQuery.data, hydrated]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const perTypeRecipients: Partial<Record<PartnerType, string[]>> = {};
      for (const t of PARTNER_TYPES) {
        const list = parseEmailList(perTypeInput[t] ?? "");
        if (list.length > 0) perTypeRecipients[t] = list;
      }
      const payload = {
        defaultRecipients: parseEmailList(defaultInput),
        perTypeRecipients,
      };
      return apiRequest("PUT", "/api/admin/partner-application-notifications", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partner-application-notifications"] });
      toast({ title: "Recipients saved", description: "Partner application alerts will use this list going forward." });
    },
    onError: (err: any) => toast({
      title: "Failed to save",
      description: parseErrorMessage(err, "Check that every entry is a valid email address."),
      variant: "destructive",
    }),
  });

  const [testType, setTestType] = useState<"default" | PartnerType>("default");
  const testMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (partnerType: "default" | PartnerType) => {
      const payload = partnerType === "default" ? {} : { partnerType };
      return apiRequest("POST", "/api/admin/partner-application-notifications/test", payload);
    },
    onSuccess: (_data, variables) => {
      const label = variables === "default" ? "default recipients" : `${variables} recipients (plus defaults)`;
      toast({
        title: "Test alert sent",
        description: `Sample partner application alert delivered to the ${label}.`,
      });
    },
    onError: (err: any) => toast({
      title: "Test send failed",
      description: parseErrorMessage(err, "Could not send the test partner application alert."),
      variant: "destructive",
    }),
  });

  return (
    <Card className="p-6 space-y-6" data-testid="panel-partner-application-notifications">
      <div>
        <h2 className="text-xl font-semibold">Partner alert recipients</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose who receives the alert email when someone submits a partner application
          or signs a partner agreement for review.
          Default recipients always receive every alert; per-type recipients are added on top for that partner type only.
          Leave everything blank to fall back to the historical default inboxes.
        </p>
      </div>

      {settingsQuery.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="partner-alerts-default">Default recipients</label>
            <p className="text-xs text-muted-foreground">Comma- or newline-separated email addresses.</p>
            <textarea
              id="partner-alerts-default"
              data-testid="input-partner-alerts-default"
              className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="alerts@padpulse.io, partnerships@padpulse.io"
              value={defaultInput}
              onChange={(e) => setDefaultInput(e.target.value)}
            />
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium">Per partner-type recipients (optional)</h3>
              <p className="text-xs text-muted-foreground">
                Add anyone who should be CC'd for a specific application type — e.g. the realtor lead for realtor applications.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {PARTNER_TYPES.map((t) => (
                <div key={t} className="space-y-1">
                  <label className="text-sm font-medium capitalize" htmlFor={`partner-alerts-${t}`}>{t}</label>
                  <textarea
                    id={`partner-alerts-${t}`}
                    data-testid={`input-partner-alerts-${t}`}
                    className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={`${t}-leads@padpulse.io`}
                    value={perTypeInput[t] ?? ""}
                    onChange={(e) => setPerTypeInput((prev) => ({ ...prev, [t]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <div className="text-xs text-muted-foreground">
              {settingsQuery.data?.updatedAt ? (
                <>Last updated {new Date(settingsQuery.data.updatedAt).toLocaleString()}{settingsQuery.data.updatedBy ? ` by ${settingsQuery.data.updatedBy}` : ""}.</>
              ) : (
                <>Not configured yet — alerts fall back to the default inboxes.</>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={testType} onValueChange={(v) => setTestType(v as "default" | PartnerType)}>
                <SelectTrigger className="h-9 w-[180px]" data-testid="select-partner-alerts-test-type">
                  <SelectValue placeholder="Test recipient set" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default" data-testid="option-partner-alerts-test-default">Default recipients</SelectItem>
                  {PARTNER_TYPES.map((t) => (
                    <SelectItem key={t} value={t} className="capitalize" data-testid={`option-partner-alerts-test-${t}`}>
                      {t} (+ defaults)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                data-testid="button-test-partner-alerts"
                onClick={() => testMutation.mutate(testType)}
                disabled={testMutation.isPending || saveMutation.isPending}
              >
                {testMutation.isPending ? "Sending…" : "Send test email"}
              </Button>
              <Button
                data-testid="button-save-partner-alerts"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? "Saving…" : "Save recipients"}
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
